import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import { useCreateBannerMutation } from "../../services/banners/banners";
import { useLazyGetPreSignedUrlQuery } from "../../services/items/items";
import { uploadImage } from "../../utils/uploadImage";
import { resolveAssetUrl } from "../../utils/assets";
import { getErrorStatus, HTTP_CONFLICT } from "../../utils/apiError";
import { onlyNumbers } from "../shared/utils";
import { BANNER_FORM_TEXTS, INPUT_LABEL_PROPS, IMAGE_BORDER_RADIUS } from "../shared/constants";
import { FormFieldGrid, FormFieldGridResponsive, SaveActionBar } from "../shared/styledComponents";
import { styled } from "@mui/material/styles";

const FormContainer = styled(Box)(({ theme }) => ({
  background: "white",
  borderRadius: theme.spacing(1),
}));

const BannerCreateForm: React.FC = () => {
  const navigate = useNavigate();
  const [createBanner, { isLoading }] = useCreateBannerMutation();
  const [getPreSignedUrl] = useLazyGetPreSignedUrlQuery();

  const [id, setId] = useState("");
  const [title, setTitle] = useState("");
  const [altText, setAltText] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [displayOrder, setDisplayOrder] = useState("0");
  const [isActive, setIsActive] = useState(true);
  const [imageUrl, setImageUrl] = useState("");

  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const imageInputRef = useRef<HTMLInputElement | null>(null);

  const openImagePicker = () => {
    setErrorMsg(null);
    if (!id.trim()) {
      setErrorMsg(BANNER_FORM_TEXTS.requiredFields);
      return;
    }
    imageInputRef.current?.click();
  };

  const handleImageSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setErrorMsg(null);
      const file = e.target.files?.[0];
      if (!file) return;

      setUploading(true);

      const presigned = await getPreSignedUrl({ id, _ts: Date.now() }).unwrap();
      await uploadImage(file, presigned.uploadUrl, presigned.headers["Content-Type"]);

      // The banner row stores the key, not a URL (EB-515); `Banner` resolves it at render.
      setImageUrl(presigned.key);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "Error subiendo la imagen.");
    } finally {
      setUploading(false);
      if (imageInputRef.current) imageInputRef.current.value = "";
    }
  };

  const handleSave = async (): Promise<void> => {
    setErrorMsg(null);

    if (!id.trim() || !title.trim() || !imageUrl.trim()) {
      setErrorMsg(BANNER_FORM_TEXTS.requiredFields);
      return;
    }

    const orderNumber = Number(displayOrder);
    if (Number.isNaN(orderNumber)) {
      setErrorMsg(BANNER_FORM_TEXTS.numericError);
      return;
    }

    try {
      await createBanner({
        id,
        title,
        image_url: imageUrl,
        link_url: linkUrl,
        alt_text: altText || title,
        display_order: orderNumber,
        is_active: isActive,
      }).unwrap();

      navigate(`/banner-maintenance/${id}`);
    } catch (err) {
      // The API now refuses to overwrite an existing id (409) rather than silently replacing a
      // live homepage banner, so the admin has to be told to pick a different one.
      if (getErrorStatus(err) === HTTP_CONFLICT) {
        setErrorMsg(BANNER_FORM_TEXTS.duplicateIdError);
        return;
      }
      setErrorMsg(err instanceof Error ? err.message : BANNER_FORM_TEXTS.saveError);
    }
  };

  return (
    <FormContainer p={2}>
      <Box mb={2}>
        <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
          {BANNER_FORM_TEXTS.createTitle}
        </Typography>
      </Box>

      {errorMsg && (
        <Box mb={2}>
          <Alert severity="error">{errorMsg}</Alert>
        </Box>
      )}

      <FormFieldGrid>
        <TextField
          label={BANNER_FORM_TEXTS.idLabel}
          value={id}
          onChange={(e) => setId(e.target.value)}
          size="small"
          fullWidth
          InputLabelProps={INPUT_LABEL_PROPS}
        />

        <TextField
          label={BANNER_FORM_TEXTS.titleLabel}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          size="small"
          fullWidth
          InputLabelProps={INPUT_LABEL_PROPS}
        />

        <TextField
          label={BANNER_FORM_TEXTS.altTextLabel}
          value={altText}
          onChange={(e) => setAltText(e.target.value)}
          size="small"
          fullWidth
          InputLabelProps={INPUT_LABEL_PROPS}
        />

        <TextField
          label={BANNER_FORM_TEXTS.linkUrlLabel}
          value={linkUrl}
          onChange={(e) => setLinkUrl(e.target.value)}
          size="small"
          fullWidth
          InputLabelProps={INPUT_LABEL_PROPS}
        />
      </FormFieldGrid>

      <FormFieldGridResponsive sx={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
        <TextField
          label={BANNER_FORM_TEXTS.displayOrderLabel}
          value={displayOrder}
          onChange={(e) => setDisplayOrder(onlyNumbers(e.target.value))}
          size="small"
          fullWidth
          inputMode="numeric"
          InputLabelProps={INPUT_LABEL_PROPS}
        />

        <FormControlLabel
          control={<Checkbox checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
          label={BANNER_FORM_TEXTS.activeLabel}
        />
      </FormFieldGridResponsive>

      <Box mt={2} p={1} sx={{ bgcolor: "background.default", borderRadius: 2 }}>
        <Button
          onClick={openImagePicker}
          variant="outlined"
          disabled={uploading}
          startIcon={<AddPhotoAlternateIcon />}
          sx={{ fontWeight: "bold", boxShadow: 1, bgcolor: "white" }}
        >
          {uploading ? BANNER_FORM_TEXTS.uploading : BANNER_FORM_TEXTS.uploadImage}
        </Button>

        {imageUrl && (
          <Box mt={1}>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              {BANNER_FORM_TEXTS.previewLabel}
            </Typography>
            <img
              src={resolveAssetUrl(imageUrl)}
              alt="Vista previa"
              style={{ maxWidth: 400, borderRadius: IMAGE_BORDER_RADIUS }}
              referrerPolicy="no-referrer"
            />
          </Box>
        )}
      </Box>

      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleImageSelected}
      />

      <SaveActionBar>
        <Button
          onClick={() => void handleSave()}
          disabled={isLoading}
          variant="outlined"
          color="success"
          startIcon={<SaveIcon />}
          fullWidth
          sx={{ fontWeight: "bold", boxShadow: 2, maxWidth: "40%", bgcolor: "white" }}
        >
          {isLoading ? BANNER_FORM_TEXTS.saving : BANNER_FORM_TEXTS.createButton}
        </Button>
      </SaveActionBar>
    </FormContainer>
  );
};

export default BannerCreateForm;
